import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getDepartments,
  getPositions,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  createPosition,
  updatePosition,
  deletePosition,
  getPositionViewableUsers
} from '../services/organizationService';
import { getUsers } from '../services/userService';
import { Department, Position, AttendanceViewScope } from '../types/organization';
import { User } from '../types/user';

const OrganizationManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'departments' | 'positions'>('departments');

  // 部署管理
  const [departments, setDepartments] = useState<Department[]>([]);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptDescription, setNewDeptDescription] = useState('');
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [showDeptCreateForm, setShowDeptCreateForm] = useState(false);

  // 役職管理
  const [positions, setPositions] = useState<Position[]>([]);
  const [newPosName, setNewPosName] = useState('');
  const [newPosDescription, setNewPosDescription] = useState('');
  const [editingPos, setEditingPos] = useState<Position | null>(null);
  const [showPosCreateForm, setShowPosCreateForm] = useState(false);

  // 権限設定モーダル
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<Position | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // 個別選択UI改善用
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // メッセージ
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isAdmin = user?.role === 'master' || user?.role === 'admin';

  useEffect(() => {
    fetchDepartments();
    fetchPositions();
    fetchUsers();
  }, []);

  const fetchDepartments = async () => {
    try {
      const data = await getDepartments();
      setDepartments(data);
    } catch (err: any) {
      console.error('部署取得エラー:', err);
      setError('部署の取得に失敗しました');
    }
  };

  const fetchPositions = async () => {
    try {
      const data = await getPositions();
      setPositions(data);
    } catch (err: any) {
      console.error('役職取得エラー:', err);
      setError('役職の取得に失敗しました');
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err: any) {
      console.error('ユーザー取得エラー:', err);
    }
  };

  // ==================== 部署管理 ====================

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeptName.trim()) {
      setError('部署名を入力してください');
      return;
    }

    try {
      await createDepartment({ name: newDeptName, description: newDeptDescription });
      setMessage('部署が作成されました');
      setNewDeptName('');
      setNewDeptDescription('');
      setShowDeptCreateForm(false);
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '部署の作成に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdateDepartment = async (dept: Department) => {
    try {
      await updateDepartment(dept.id, {
        name: dept.name,
        description: dept.description
      });
      setMessage('部署情報が更新されました');
      setEditingDept(null);
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '部署の更新に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeleteDepartment = async (id: number, name: string) => {
    if (!window.confirm(`部署「${name}」を削除してもよろしいですか？`)) return;

    try {
      await deleteDepartment(id);
      setMessage('部署が削除されました');
      fetchDepartments();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '部署の削除に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  // ==================== 役職管理 ====================

  const handleCreatePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPosName.trim()) {
      setError('役職名を入力してください');
      return;
    }

    try {
      await createPosition({ name: newPosName, description: newPosDescription });
      setMessage('役職が作成されました');
      setNewPosName('');
      setNewPosDescription('');
      setShowPosCreateForm(false);
      fetchPositions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '役職の作成に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdatePosition = async (pos: Position) => {
    try {
      await updatePosition(pos.id, {
        name: pos.name,
        description: pos.description,
        canApproveCorrections: pos.canApproveCorrections,
        canApproveOwnRequests: pos.canApproveOwnRequests,
        attendanceViewScope: pos.attendanceViewScope
      });
      setMessage('役職情報が更新されました');
      setEditingPos(null);
      fetchPositions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '役職の更新に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeletePosition = async (id: number, name: string) => {
    if (!window.confirm(`役職「${name}」を削除してもよろしいですか？`)) return;

    try {
      await deletePosition(id);
      setMessage('役職が削除されました');
      fetchPositions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '役職の削除に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleOpenPermissionModal = async (pos: Position) => {
    setEditingPermissions(pos);
    setPermissionModalOpen(true);

    // customの場合、既存の選択ユーザーを取得
    if (pos.attendanceViewScope === 'custom') {
      try {
        const viewableUserIds = await getPositionViewableUsers(pos.id);
        setSelectedUserIds(viewableUserIds);
      } catch (err: any) {
        console.error('閲覧可能ユーザー取得エラー:', err);
        setSelectedUserIds([]);
      }
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleClosePermissionModal = () => {
    setPermissionModalOpen(false);
    setEditingPermissions(null);
    setSelectedUserIds([]);
    setUserSearchQuery('');
  };

  const handleSavePermissions = async () => {
    if (!editingPermissions) return;

    const updateData = {
      name: editingPermissions.name,
      description: editingPermissions.description,
      canApproveCorrections: editingPermissions.canApproveCorrections,
      canApproveOwnRequests: editingPermissions.canApproveOwnRequests,
      canSubmitForOthers: editingPermissions.canSubmitForOthers,
      canExportAttendance: editingPermissions.canExportAttendance,
      attendanceViewScope: editingPermissions.attendanceViewScope,
      viewableUserIds: editingPermissions.attendanceViewScope === 'custom' ? selectedUserIds : undefined
    };

    try {
      await updatePosition(editingPermissions.id, updateData);
      setMessage('権限設定が更新されました');
      handleClosePermissionModal();
      fetchPositions();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      console.error('権限設定の更新エラー:', err);
      setError(err.response?.data?.error || '権限設定の更新に失敗しました');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleToggleDepartmentUsers = (deptId: number | null) => {
    const deptUsers = getFilteredUsersByDepartment(deptId).map(u => u.id);
    const allSelected = deptUsers.every(id => selectedUserIds.includes(id));

    if (allSelected) {
      // 全て選択済み → 全て解除
      setSelectedUserIds(prev => prev.filter(id => !deptUsers.includes(id)));
    } else {
      // 一部または未選択 → 全て選択
      setSelectedUserIds(prev => Array.from(new Set([...prev, ...deptUsers])));
    }
  };

  const getFilteredUsersByDepartment = (deptId: number | null) => {
    const deptUsers = users.filter(u => u.department_id === deptId);
    if (!userSearchQuery) return deptUsers;

    const query = userSearchQuery.toLowerCase();
    return deptUsers.filter(u =>
      u.name.toLowerCase().includes(query) ||
      u.username.toLowerCase().includes(query)
    );
  };

  const getViewScopeLabel = (scope: AttendanceViewScope): string => {
    switch (scope) {
      case 'self': return '自分のみ';
      case 'department': return '所属部署内';
      case 'company': return '全社員';
      case 'custom': return '個別選択';
      default: return '不明';
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: '1200px', margin: '50px auto', padding: '20px' }}>
        <h1>アクセス権限がありません</h1>
        <p>この画面を表示する権限がありません。</p>
        <button onClick={() => navigate('/dashboard')} style={{ padding: '8px 16px' }}>
          ダッシュボードに戻る
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>組織管理</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              whiteSpace: 'nowrap'
            }}
          >
            ダッシュボードに戻る
          </button>
        </div>
      </header>

      {message && (
        <div style={{
          color: '#155724',
          backgroundColor: '#d4edda',
          marginBottom: '20px',
          padding: '10px',
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}

      {error && (
        <div style={{
          color: '#721c24',
          backgroundColor: '#f8d7da',
          marginBottom: '20px',
          padding: '10px',
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {/* タブ */}
      <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: '20px' }}>
        <button
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            fontWeight: activeTab === 'departments' ? 'bold' : 'normal',
            border: 'none',
            borderBottom: activeTab === 'departments' ? '3px solid #007bff' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'departments' ? '#007bff' : '#6c757d',
            cursor: 'pointer'
          }}
          onClick={() => setActiveTab('departments')}
        >
          部署管理
        </button>
        <button
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            fontWeight: activeTab === 'positions' ? 'bold' : 'normal',
            border: 'none',
            borderBottom: activeTab === 'positions' ? '3px solid #007bff' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'positions' ? '#007bff' : '#6c757d',
            cursor: 'pointer'
          }}
          onClick={() => setActiveTab('positions')}
        >
          役職管理
        </button>
      </div>

      {/* 部署管理タブ */}
      {activeTab === 'departments' && (
        <div>
          {!showDeptCreateForm && (
            <button
              onClick={() => setShowDeptCreateForm(true)}
              style={{
                marginBottom: '20px',
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              + 新しい部署を追加
            </button>
          )}

          {showDeptCreateForm && (
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
              <h2>新しい部署を追加</h2>
              <form onSubmit={handleCreateDepartment}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div>
                    <label htmlFor="dept-name">部署名 <span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="dept-name"
                      value={newDeptName}
                      onChange={(e) => setNewDeptName(e.target.value)}
                      placeholder="例: 営業部"
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="dept-desc">説明</label>
                    <input
                      type="text"
                      id="dept-desc"
                      value={newDeptDescription}
                      onChange={(e) => setNewDeptDescription(e.target.value)}
                      placeholder="例: 営業活動を担当"
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    追加
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeptCreateForm(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>部署名</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>説明</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => {
                const isEditing = editingDept?.id === dept.id;
                return (
                  <tr key={dept.id} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: isEditing ? '#fff3cd' : 'transparent' }}>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingDept.name}
                          onChange={(e) => setEditingDept({ ...editingDept, name: e.target.value })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                        />
                      ) : (
                        dept.name
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingDept.description || ''}
                          onChange={(e) => setEditingDept({ ...editingDept, description: e.target.value })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                        />
                      ) : (
                        dept.description || '-'
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => handleUpdateDepartment(editingDept)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            保存
                          </button>
                          <button
                            onClick={() => setEditingDept(null)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            キャンセル
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => setEditingDept(dept)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            編集
                          </button>
                          <button
                            onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            削除
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {departments.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    部署が登録されていません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 役職管理タブ */}
      {activeTab === 'positions' && (
        <div>
          {!showPosCreateForm && (
            <button
              onClick={() => setShowPosCreateForm(true)}
              style={{
                marginBottom: '20px',
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              + 新しい役職を追加
            </button>
          )}

          {showPosCreateForm && (
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
              <h2>新しい役職を追加</h2>
              <form onSubmit={handleCreatePosition}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div>
                    <label htmlFor="pos-name">役職名 <span style={{ color: 'red' }}>*</span></label>
                    <input
                      type="text"
                      id="pos-name"
                      value={newPosName}
                      onChange={(e) => setNewPosName(e.target.value)}
                      placeholder="例: 課長"
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="pos-desc">説明</label>
                    <input
                      type="text"
                      id="pos-desc"
                      value={newPosDescription}
                      onChange={(e) => setNewPosDescription(e.target.value)}
                      placeholder="例: 課の管理を担当"
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    追加
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPosCreateForm(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>役職名</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>説明</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>承認権限</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>閲覧範囲</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => {
                const isEditing = editingPos?.id === pos.id;
                const isSystemAdmin = pos.name === '管理者';
                return (
                  <tr key={pos.id} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: isEditing ? '#fff3cd' : 'transparent' }}>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingPos.name}
                          onChange={(e) => setEditingPos({ ...editingPos, name: e.target.value })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                        />
                      ) : (
                        <>
                          {pos.name}
                          {isSystemAdmin && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>(システム)</span>}
                        </>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingPos.description || ''}
                          onChange={(e) => setEditingPos({ ...editingPos, description: e.target.value })}
                          style={{ width: '100%', padding: '4px', border: '1px solid #dee2e6', borderRadius: '4px' }}
                        />
                      ) : (
                        pos.description || '-'
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ color: pos.canApproveCorrections ? '#007bff' : '#6c757d' }}>
                        {pos.canApproveCorrections ? 'あり' : 'なし'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {getViewScopeLabel(pos.attendanceViewScope)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => handleUpdatePosition(editingPos)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            保存
                          </button>
                          <button
                            onClick={() => setEditingPos(null)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            キャンセル
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={() => handleOpenPermissionModal(pos)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#6f42c1',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            権限設定
                          </button>
                          {!isSystemAdmin && (
                            <>
                              <button
                                onClick={() => setEditingPos(pos)}
                                style={{
                                  padding: '5px 10px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                編集
                              </button>
                              <button
                                onClick={() => handleDeletePosition(pos.id, pos.name)}
                                style={{
                                  padding: '5px 10px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                              >
                                削除
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {positions.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    役職が登録されていません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 権限設定モーダル */}
      {permissionModalOpen && editingPermissions && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleClosePermissionModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '20px', borderBottom: '1px solid #dee2e6' }}>
              <h2 style={{ margin: 0 }}>権限設定: {editingPermissions.name}</h2>
            </div>

            <div style={{ padding: '20px' }}>
              {/* 承認権限 */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingPermissions.canApproveCorrections}
                    onChange={(e) =>
                      setEditingPermissions({
                        ...editingPermissions,
                        canApproveCorrections: e.target.checked
                      })
                    }
                    style={{ width: '18px', height: '18px', marginRight: '10px' }}
                  />
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>打刻修正申請の承認権限</span>
                </label>
                <p style={{ margin: '8px 0 0 28px', color: '#6c757d', fontSize: '14px' }}>
                  この役職のユーザーは、閲覧範囲内のユーザーの打刻修正申請を承認/却下できます
                </p>
              </div>

              {/* 自己承認許可 */}
              {editingPermissions.canApproveCorrections && (
                <div style={{ marginBottom: '25px', marginLeft: '28px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingPermissions.canApproveOwnRequests}
                      onChange={(e) =>
                        setEditingPermissions({
                          ...editingPermissions,
                          canApproveOwnRequests: e.target.checked
                        })
                      }
                      style={{ width: '18px', height: '18px', marginRight: '10px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>自身の打刻修正申請の承認を許可する</span>
                  </label>
                  <p style={{ margin: '8px 0 0 28px', color: '#6c757d', fontSize: '14px' }}>
                    有効にすると、この役職のユーザーは自分で申請した打刻修正を自分で承認できます
                  </p>
                </div>
              )}

              {/* 代理申請許可 */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingPermissions.canSubmitForOthers}
                    onChange={(e) =>
                      setEditingPermissions({
                        ...editingPermissions,
                        canSubmitForOthers: e.target.checked
                      })
                    }
                    style={{ width: '18px', height: '18px', marginRight: '10px' }}
                  />
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>閲覧範囲内のユーザーの代理申請を許可する</span>
                </label>
                <p style={{ margin: '8px 0 0 28px', color: '#6c757d', fontSize: '14px' }}>
                  有効にすると、この役職のユーザーは閲覧範囲内のユーザーの打刻修正申請を代理で行えます
                </p>
              </div>

              {/* 勤怠エクスポート許可 */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingPermissions.canExportAttendance}
                    onChange={(e) =>
                      setEditingPermissions({
                        ...editingPermissions,
                        canExportAttendance: e.target.checked
                      })
                    }
                    style={{ width: '18px', height: '18px', marginRight: '10px' }}
                  />
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>勤怠データのエクスポートを許可する</span>
                </label>
                <p style={{ margin: '8px 0 0 28px', color: '#6c757d', fontSize: '14px' }}>
                  有効にすると、この役職のユーザーは閲覧範囲内のユーザーの勤怠データをCSV/PDF形式でエクスポートできます
                </p>
              </div>

              {/* 勤怠閲覧範囲 */}
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                  勤怠閲覧範囲
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(['self', 'department', 'company', 'custom'] as AttendanceViewScope[]).map(scope => (
                    <label key={scope} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="viewScope"
                        value={scope}
                        checked={editingPermissions.attendanceViewScope === scope}
                        onChange={(e) =>
                          setEditingPermissions({
                            ...editingPermissions,
                            attendanceViewScope: e.target.value as AttendanceViewScope
                          })
                        }
                        style={{ width: '16px', height: '16px', marginRight: '10px' }}
                      />
                      <span>{getViewScopeLabel(scope)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 個別選択UI */}
              {editingPermissions.attendanceViewScope === 'custom' && (
                <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
                  <label style={{ display: 'block', fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                    閲覧可能なユーザーを選択
                  </label>

                  {/* 検索ボックス */}
                  <div style={{ marginBottom: '15px' }}>
                    <input
                      type="text"
                      placeholder="氏名またはログインIDで検索..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '15px'
                  }}>
                    {/* 部署なしのユーザー */}
                    {getFilteredUsersByDepartment(null).length > 0 && (
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            checked={getFilteredUsersByDepartment(null).every(u => selectedUserIds.includes(u.id))}
                            onChange={() => handleToggleDepartmentUsers(null)}
                            style={{ width: '16px', height: '16px', marginRight: '8px' }}
                          />
                          <span style={{ fontWeight: 'bold', color: '#495057' }}>部署未設定</span>
                        </div>
                        <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          {getFilteredUsersByDepartment(null).map(u => (
                            <label key={u.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(u.id)}
                                onChange={() => handleToggleUserSelection(u.id)}
                                style={{ width: '16px', height: '16px', marginRight: '8px' }}
                              />
                              <span style={{ fontSize: '14px' }}>{u.name} ({u.username})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 部署ごとのユーザー */}
                    {departments.map(dept => {
                      const deptUsers = getFilteredUsersByDepartment(dept.id);
                      if (deptUsers.length === 0) return null;
                      return (
                        <div key={dept.id} style={{ marginBottom: '15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <input
                              type="checkbox"
                              checked={deptUsers.every(u => selectedUserIds.includes(u.id))}
                              onChange={() => handleToggleDepartmentUsers(dept.id)}
                              style={{ width: '16px', height: '16px', marginRight: '8px' }}
                            />
                            <span style={{ fontWeight: 'bold', color: '#495057' }}>{dept.name}</span>
                          </div>
                          <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            {deptUsers.map(u => (
                              <label key={u.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedUserIds.includes(u.id)}
                                  onChange={() => handleToggleUserSelection(u.id)}
                                  style={{ width: '16px', height: '16px', marginRight: '8px' }}
                                />
                                <span style={{ fontSize: '14px' }}>{u.name} ({u.username})</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {users.filter(u => {
                      const query = userSearchQuery.toLowerCase();
                      return !query || u.name.toLowerCase().includes(query) || u.username.toLowerCase().includes(query);
                    }).length === 0 && (
                      <p style={{ textAlign: 'center', color: '#6c757d', margin: '20px 0' }}>
                        検索結果が見つかりません
                      </p>
                    )}
                  </div>

                  <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#6c757d' }}>
                    選択中: {selectedUserIds.length}名
                  </p>
                </div>
              )}
            </div>

            <div style={{ padding: '20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={handleClosePermissionModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleSavePermissions}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagementPage;
